/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */

import Sidebar from '@univdiam/ui/customComponents/Sidebar/Sidebar'
import BottomNav from '@univdiam/ui/customComponents/Sidebar/BottomNav'
import Drawer from '@univdiam/ui/customComponents/Drawer'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import TicketIcon from '@/assets/FeaturedIcon.svg'
import { OrderForm, orderFormSchema } from '@univdiam/ui/customComponents/forms/CreateOrder'
import z from 'zod'
import main_logo from '../assets/univdiam.png'
import { ScrollArea, ScrollBar } from '@univdiam/ui/components/scroll-area'
import { useAuthStore, useOrderStore, useSessionStore } from '@/store'
import api from '@/http/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast, Toaster } from 'sonner'
import { useSocketStore } from '@/socketStore'
import type { NotificationType } from '@/types'
import { Bell } from 'lucide-react'
import { AlertDialog, AlertDialogContent, } from '@univdiam/ui/components/alert-dialog'
import { Button } from '@univdiam/ui/components/button'
import { isOppositeSide } from '@univdiam/ui/lib/chatSides'

const logoutApi = () => {
    return api.post(`/customer/logout`);
}

const orderCreation = (data: z.infer<typeof orderFormSchema>) => {
    return api.post(`/order/create`, data);
}

const DashboardLayout = () => {
    const { logout, user, setNotificationCount, notificationCount, setChatUnreadCount } = useAuthStore()
    const { createOrder, setCreateOrder } = useOrderStore()
    const { sessionExpired, setSessionExpired } = useSessionStore();
    const queryClient = useQueryClient()

    const connect = useSocketStore((s) => s.connect)
    const disconnect = useSocketStore((s) => s.disconnect)
    const sendNotification = useSocketStore((s) => s.sendNotification)
    const recieveNotification = useSocketStore((s) => s.recieveNotification)
    const isConnected = useSocketStore((s) => s.isConnected)

    const { mutate, isError, isPending, error } = useMutation({
        mutationFn: (values: z.infer<typeof orderFormSchema>) => {
            return orderCreation(values)
        },
        onSuccess: async ({ data }) => {
            toast.success('Order created successfully!', {
                position: 'top-center'
            })

            sendNotification({
                action_by: user?.role!,
                action_by_id: user?.customer_id.toString()!,
                action_type: 'order_created',
                order_id: data.order.id,
                title: `Order #${data.order.order_id} created`,
                metadata: {
                    order_ref: data.order.id,
                    order_id: data.order.order_id,
                    customer_id: user?.customer_id.toString()!,
                    customer_name: user?.full_name!,
                    job_title: data.order.job_title
                },

            })
            queryClient.invalidateQueries({ queryKey: ['orders'] })
            setCreateOrder(false)
        }
    })


    const handleCreateOrder = (values: z.infer<typeof orderFormSchema>) => {
        mutate(values)
    }
    const navigate = useNavigate()
    // Pages scroll inside the ScrollArea viewport, not the window, and that
    // viewport outlives route changes -- without this a product opened from
    // low down the catalog list opens scrolled to the same depth.
    const { pathname } = useLocation()
    const scrollRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        scrollRef.current?.querySelector('[data-slot=scroll-area-viewport]')?.scrollTo(0, 0)
    }, [pathname])

    const { mutate: logoutMutation } = useMutation({
        mutationFn: logoutApi,
        onSuccess: () => {
            logout()
            navigate('/login')
        }
    })

    const logoutUser = () => {
        logoutMutation()
    }

    const [isLoading, setIsLoading] = useState(false)

    const handleSendToUpload = async (files: File[]): Promise<string[]> => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });

            const response = await api.post(
                `/upload?customerId=${user?.customer_id}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                }
            );

            const data = response.data;
            return data.files ?? [];

        } catch (error) {
            console.error('Upload failed:', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    };



    useEffect(() => {
        connect()

        return () => {
            disconnect()
        }
    }, [connect, disconnect])

    // Reconcile notification count after socket connects
    useEffect(() => {
        if (isConnected) {
            api.get('/customer/self').then(res => {
                if (res.data.unseenNotifications !== undefined) {
                    setNotificationCount(res.data.unseenNotifications)
                }
            }).catch(console.error)
        }
    }, [isConnected])

    // Fetch chat unread count (own key so refetchInterval never gets overridden)
    const { data: chatsData } = useQuery({
        queryKey: ["sidebar-chat-unread"],
        queryFn: async () => {
            const res = await api.get(`/chat/order/unread-count`);
            return res.data?.data?.unread_count as number;
        },
        refetchInterval: 10000,
    });

    const chatUnreadCount = useMemo(() => {
        return chatsData || 0;
    }, [chatsData]);

    // Sync unread count to auth store
    useEffect(() => { setChatUnreadCount(chatUnreadCount) }, [chatUnreadCount, setChatUnreadCount]);

    useEffect(() => {
        const unsubscribe = recieveNotification((data: unknown) => {
            const payload = data as NotificationType

            // Handle chat read receipts silently — optimistic update for instant UI
            if (payload.action_type === 'chat_read') {
                // Only an OPPOSITE-side read means the viewer's own messages were seen.
                // unread_count is NOT touched: a chat_read is broadcast to every other
                // admin too, so zeroing it here re-creates the shared-badge bug.
                if (isOppositeSide(user?.role, payload.action_by)) {
                    queryClient.setQueryData(['all-customer-order-chats'], (oldData: any) => {
                        if (!oldData?.data?.orderChat) return oldData;
                        return {
                            ...oldData,
                            data: {
                                ...oldData.data,
                                orderChat: oldData.data.orderChat.map((chat: any) =>
                                    String(chat.order_id) === String(payload.order_id)
                                        ? { ...chat, is_read: 1 }
                                        : chat
                                )
                            }
                        };
                    });
                }
                queryClient.invalidateQueries({ queryKey: ["all-customer-order-chats"] });
                return;
            }

            setNotificationCount(payload.unseenCount!)
            queryClient.invalidateQueries({ queryKey: ['notifications'] })
            queryClient.invalidateQueries({ queryKey: ['orders'] })

            if (payload.action_type === 'chat_message') {
                queryClient.invalidateQueries({ queryKey: ['all-customer-order-chats'] })
                queryClient.invalidateQueries({ queryKey: ['sidebar-chat-unread'] })
            }

            toast.info(<p onClick={() => navigate('/notifications')} className='cursor-pointer'>New Notification: {payload.title}</p>, {
                position: 'top-center',

            })
        });
        return () => {
            unsubscribe();
        };
    }, [recieveNotification, queryClient, navigate, setNotificationCount, user?.role]);
    return (
        <div className='flex flex-col md:flex-row h-screen w-full bg-[#F6F6F6]'>
            {/* Mobile Header */}
            <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-white/90 backdrop-blur-md border-b border-border/50 shadow-sm flex items-center justify-between px-4 z-50">
                <img src={main_logo} alt="logo" className="h-7 w-auto object-contain" />
                <button 
                  onClick={() => navigate('/notifications')}
                  className="relative p-2.5 bg-muted/30 hover:bg-muted/60 active:scale-95 rounded-full text-foreground transition-all duration-200"
                >
                    <Bell size={22} strokeWidth={1.5} />
                    {notificationCount > 0 && (
                        <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white ring-2 ring-white">
                            {notificationCount > 99 ? '99+' : notificationCount}
                        </span>
                    )}
                </button>
            </header>

            <Sidebar {...{ setCreateTicket: setCreateOrder, main_logo, navigate, logoutUser, notificationCount, chatUnreadCount, user }} />
            <div ref={scrollRef} className='flex-1 h-full overflow-hidden pt-14 md:pt-0 pb-24 md:pb-0'>
                <ScrollArea className='h-full w-full [&>[data-slot=scroll-area-viewport]>div]:h-full'>
                    <Outlet />
                    <ScrollBar orientation="horizontal" />
                </ScrollArea>
            </div>
            <BottomNav {...{ navigate, setCreateTicket: setCreateOrder, notificationCount, chatUnreadCount, user }} />
            <Drawer open={createOrder} setOpen={setCreateOrder} title={
                <p className='flex items-center gap-[8px]'>
                    <img src={TicketIcon} alt="Create Order" />
                    Create a Custom Order
                </p>
            }>
                <OrderForm onSubmit={handleCreateOrder} handleSendToUpload={handleSendToUpload} mediaLoading={isLoading} isError={isError} isLoading={isPending} error={error} />
            </Drawer>

            <Toaster />
        </div>
    )
}

export default DashboardLayout